import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const ChatGPT4Settings = ({ modelSettings, setModelSettings }) => {
  // Define state variables for each setting
  const [temperature, setTemperature] = useState(modelSettings.temperature || 0.85);
  const [maxTokens, setMaxTokens] = useState(modelSettings.maxTokens || 3600);
  const [modelVersion, setModelVersion] = useState(modelSettings.modelVersion || 'gpt-3.5-turbo-16k');
  const [isStreamingEnabled, setIsStreamingEnabled] = useState(modelSettings.stream || false);

  useEffect(() => {
    // Save settings to parent state when any setting changes
    setModelSettings((prevSettings) => ({
      ...prevSettings,
      temperature,
      maxTokens,
      modelVersion,
      stream: isStreamingEnabled,
    }));
    sessionStorage.setItem('modelSettings', JSON.stringify({
      ...modelSettings,
      temperature,
      maxTokens,
      modelVersion,
      stream: isStreamingEnabled,
    }));
  }, [
    temperature,
    maxTokens,
    modelVersion,
    isStreamingEnabled,
  ]);

  return (
    <div>
      <h2>Model Settings</h2>
      <table>
        <thead>
          <tr>
            <th>Index</th>
            <th>Setting</th>
            <th>Description</th>
            <th>Typical Setting Value</th>
            <th>Value Range</th>
            <th>User Input</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>1</td>
            <td>Model Version</td>
            <td>Select the model version</td>
            <td>gpt-3.5-turbo-16k</td>
            <td>GPT Models</td>
            <td>
              <select
                value={modelVersion}
                onChange={(e) => setModelVersion(e.target.value)}
              >
                <option value="gpt-3.5-turbo-16k">gpt-3.5-turbo-16k</option>
                <option value="gpt-4">GPT-4</option>
                <option value="gpt-4-32k-0613">GPT-4-32k-0613</option>
                <option value="gpt-4-1106-preview">gpt-4-1106-preview</option>
              </select>
            </td>
          </tr>
          <tr>
            <td>2</td>
            <td>Temperature</td>
            <td>Controls randomness. Higher values produce more random outputs.</td>
            <td>0.85</td>
            <td>0.0 to 2.0</td>
            <td>
              <input
                type="number"
                min="0"
                max="2.00"
                step="0.05"
                value={temperature}
                onChange={(e) => setTemperature(parseFloat(e.target.value))}
              />
            </td>
          </tr>
          <tr>
            <td>3</td>
            <td>Max Tokens</td>
            <td>Maximum number of tokens (words/pieces of words) in the output.</td>
            <td>4000</td>
            <td>100 to 8192</td>
            <td>
              <input
                type="number"
                min="100"
                max="8192"
                step="100"
                value={maxTokens}
                onChange={(e) => setMaxTokens(parseInt(e.target.value, 10))}
              />
            </td>
          </tr>
          <tr>
            <td>4</td>
            <td>Enable Streaming</td>
            <td>Enable/Disable model streaming</td>
            <td>Disabled</td>
            <td>Enabled/Disabled</td>
            <td>
              <input
                type="checkbox"
                checked={isStreamingEnabled}
                onChange={(e) => setIsStreamingEnabled(e.target.checked)}
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// Validate the props
ChatGPT4Settings.propTypes = {
  modelSettings: PropTypes.shape({
    temperature: PropTypes.number,
    maxTokens: PropTypes.number,
    modelVersion: PropTypes.string,
    role: PropTypes.string,
    topP: PropTypes.number,
    frequencyPenalty: PropTypes.number,
    presencePenalty: PropTypes.number,
    stopSequences: PropTypes.arrayOf(PropTypes.string),
    echo: PropTypes.bool,
    stream: PropTypes.bool,
    bestOf: PropTypes.number,
  }).isRequired,
  setModelSettings: PropTypes.func.isRequired,
};

export default ChatGPT4Settings;
